import { zodResolver } from '@hookform/resolvers/zod';
import { m } from '@pricedrop/i18n';
import { createFileRoute } from '@tanstack/react-router';
import CheckCircle2 from 'lucide-react/dist/esm/icons/check-circle-2';
import Home from 'lucide-react/dist/esm/icons/home';
import Mail from 'lucide-react/dist/esm/icons/mail';
import Send from 'lucide-react/dist/esm/icons/send';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ApiError, api, fetchApi } from '@/lib/api';
import { buildCanonicalLink, buildMeta } from '@/lib/seo';
import { ssrForPublicOnly } from '@/lib/ssr-utils';

export const Route = createFileRoute('/contact/')({
  ssr: ssrForPublicOnly,
  head: () => ({
    meta: buildMeta({
      title: 'צור קשר - Pricedrop',
      description: 'יש לכם שאלה או משוב על Pricedrop? צרו קשר ונחזור אליכם בהקדם.',
      canonicalUrl: '/contact',
    }),
    links: [buildCanonicalLink('/contact')],
  }),
  component: ContactPage,
});

type ContactFormValues = {
  name: string;
  email: string;
  message: string;
};

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: {
          sitekey: string;
          callback?: (token: string) => void;
          'error-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
        },
      ) => string;
      reset: (widgetId: string) => void;
      getResponse: (widgetId: string) => string;
    };
  }
}

function ContactPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string>('');

  const contactSchema = z.object({
    name: z.string().min(2, m.contact_validation_nameMin()),
    email: z.string().email(m.contact_validation_emailInvalid()),
    message: z.string().min(10, m.contact_validation_messageMin()),
  });

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: '',
      email: '',
      message: '',
    },
  });

  useEffect(() => {
    const user = 'contact';
    const domain = 'pricedrop';
    const tld = 'co.il';
    setContactEmail(`${user}@${domain}.${tld}`);
  }, []);

  useEffect(() => {
    if (isSubmitted || !TURNSTILE_SITE_KEY) return;

    const existingScript = document.querySelector('script[src*="turnstile"]');

    const renderWidget = () => {
      if (turnstileRef.current && window.turnstile && !widgetIdRef.current) {
        widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token: string) => {
            setTurnstileToken(token);
          },
          'error-callback': () => {
            toast.error(m.contact_turnstileFailed());
          },
          theme: 'auto',
        });
      }
    };

    if (existingScript) {
      renderWidget();
    } else {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      script.onload = renderWidget;
      document.body.appendChild(script);
    }
  }, [isSubmitted]);

  async function onSubmit(values: ContactFormValues) {
    setIsSubmitting(true);

    try {
      if (TURNSTILE_SITE_KEY && !turnstileToken) {
        toast.error(m.contact_turnstileRequired());
        setIsSubmitting(false);
        return;
      }

      await fetchApi(
        api.api.contact.post({
          name: values.name,
          email: values.email,
          message: values.message,
          turnstileToken: turnstileToken,
        }),
      );

      setIsSubmitted(true);
    } catch (error) {
      if (error instanceof ApiError) {
        toast.error(error.message);
      } else {
        console.error('Contact form error:', error);
        toast.error(m.contact_submitError());
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isSubmitted) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-12">
        <div className="flex flex-col items-center justify-center gap-6 py-12 text-center">
          <div className="relative">
            <div className="absolute inset-0 animate-pulse rounded-full bg-green-500/20 blur-2xl" />
            <div className="relative rounded-full border-4 border-green-500 bg-gradient-to-br from-green-50 to-emerald-50 p-8 dark:border-green-400 dark:from-green-950/50 dark:to-emerald-950/50">
              <CheckCircle2 className="h-20 w-20 text-green-600 dark:text-green-400" />
            </div>
          </div>

          <div className="space-y-3">
            <h1 className="text-4xl font-bold">{m.contact_success_title()}</h1>
            <p className="text-xl text-muted-foreground">{m.contact_success_subtitle()}</p>
          </div>

          <div className="w-full max-w-md space-y-4 rounded-lg border bg-muted/50 p-6">
            <div className="flex items-start gap-3">
              <Mail className="mt-0.5 h-5 w-5 shrink-0 text-blue-600 dark:text-blue-400" />
              <div className="text-start">
                <h3 className="mb-1 font-semibold">{m.contact_success_whatNext()}</h3>
                <p className="text-sm text-muted-foreground">{m.contact_success_responseTime()}</p>
              </div>
            </div>
          </div>

          <div className="pt-4">
            <Button
              onClick={() => (window.location.href = '/')}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
            >
              <Home className="ms-2 h-4 w-4" />
              {m.contact_success_backToHome()}
            </Button>
          </div>

          <div className="w-full max-w-md border-t pt-6">
            <p className="text-sm text-muted-foreground">
              {m.contact_success_urgentQuestion()}{' '}
              <a
                href="/faq"
                className="font-medium text-blue-700 underline hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
              >
                {m.contact_success_checkFaq()}
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-4xl px-4 py-12">
      <div className="mb-12 text-center">
        <h1 className="mb-4 text-4xl font-bold">{m.contact_title()}</h1>
        <p className="text-lg text-muted-foreground">{m.contact_subtitle()}</p>
      </div>

      <div className="grid gap-8 md:grid-cols-2">
        <div className="space-y-6">
          <div>
            <h2 className="mb-6 text-2xl font-semibold">{m.contact_contactMethods()}</h2>

            <div className="space-y-4">
              <div className="flex items-start gap-4 rounded-lg border bg-muted/50 p-4">
                <Mail className="mt-1 h-6 w-6 text-blue-600 dark:text-blue-400" />
                <div>
                  <h3 className="mb-1 font-semibold">{m.contact_emailLabel()}</h3>
                  <p className="text-sm text-muted-foreground">
                    {contactEmail && (
                      <a
                        href={`mailto:${contactEmail}`}
                        className="underline transition-colors hover:text-blue-700 dark:hover:text-blue-400"
                      >
                        {contactEmail}
                      </a>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-lg border bg-gradient-to-br from-blue-50 to-indigo-50 p-6 dark:from-blue-950/30 dark:to-indigo-950/30">
            <h3 className="mb-3 font-semibold">{m.contact_faqHeading()}</h3>
            <p className="mb-4 text-sm text-muted-foreground">
              {m.contact_faqSuggestion()}
              <a
                href="/faq"
                className="font-medium text-blue-700 underline hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
              >
                {' '}
                {m.contact_faqLink()}
              </a>
            </p>
          </div>
        </div>

        <div>
          <h2 className="mb-6 text-2xl font-semibold">{m.contact_sendMessage()}</h2>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{m.contact_form_fullName()}</FormLabel>
                    <FormControl>
                      <Input placeholder={m.contact_form_fullNamePlaceholder()} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{m.contact_form_emailLabel()}</FormLabel>
                    <FormControl>
                      <Input type="email" dir="ltr" placeholder="your@email.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{m.contact_form_message()}</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={m.contact_form_messagePlaceholder()}
                        rows={6}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {TURNSTILE_SITE_KEY ? (
                <div className="flex justify-center">
                  <div ref={turnstileRef} />
                </div>
              ) : (
                <p className="text-center text-xs text-muted-foreground">
                  Turnstile disabled (VITE_TURNSTILE_SITE_KEY not set)
                </p>
              )}

              <Button
                type="submit"
                disabled={isSubmitting || (!!TURNSTILE_SITE_KEY && !turnstileToken)}
                className="w-full cursor-pointer bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              >
                {isSubmitting ? (
                  m.contact_form_submitting()
                ) : (
                  <>
                    <Send className="me-2 h-4 w-4" />
                    {m.contact_form_submit()}
                  </>
                )}
              </Button>
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
}
